// AccountingDashboard.jsx
import React, { Component } from "react";
import axios from "axios";
import {
  Card,
  Row,
  Col,
  Alert,
  Spinner,
  Form,
  Button,
  ButtonGroup,
} from "react-bootstrap";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
} from "chart.js";

import CartContext from "./CartContext";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement
);

export default class AccountingDashboard extends Component {
  static contextType = CartContext;

  state = {
    loading: true,
    error: null,
    filter: "year", // today | week | month | year | custom
    from: new Date(new Date().getFullYear(), 0, 1),
    to: new Date(),
    summary: null,
    cashflow: [],
    revenueBreakdown: [],
    expenseBreakdown: [],
    topExpenses: [],
  };

  componentDidMount() {
    window.scrollTo(0, 0);
    this.applyFilter("year");
  }

  iso(date) {
    return new Date(date).toISOString().substring(0, 10);
  }

  setRange(from, to, filter) {
    this.setState({ from, to, filter }, () => this.loadAll());
  }

  applyFilter = (filter) => {
    const today = new Date();
    let from = today;
    let to = today;

    switch (filter) {
      case "today":
        from = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        to = today;
        break;
      case "week": {
        const day = today.getDay(); // 0=Sun
        const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Monday start
        from = new Date(today.setDate(diff));
        to = new Date();
        break;
      }
      case "month":
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        to = new Date();
        break;
      case "year":
        from = new Date(today.getFullYear(), 0, 1);
        to = new Date();
        break;
      case "custom":
        from = this.state.from;
        to = this.state.to;
        break;
      default:
        from = new Date(today.getFullYear(), 0, 1);
        to = today;
    }

    this.setRange(from, to, filter);
  };

  async loadAll() {
    try {
      this.setState({ loading: true, error: null });
      const storeId = this.context.storeId;
      const params = {
        storeId,
        from: this.iso(this.state.from),
        to: this.iso(this.state.to),
      };

      const [summary, cashflow, revCat, expCat, topExp] = await Promise.all([
        axios.get(process.env.REACT_APP_API + "accounting/summary", { params }),
        axios.get(process.env.REACT_APP_API + "accounting/monthly-cashflow", {
          params,
        }),
        axios.get(process.env.REACT_APP_API + "accounting/category-breakdown", {
          params: { ...params, type: "Revenue" },
        }),
        axios.get(process.env.REACT_APP_API + "accounting/category-breakdown", {
          params: { ...params, type: "Expense" },
        }),
        axios.get(process.env.REACT_APP_API + "accounting/top-expenses", {
          params,
        }),
      ]);

      this.setState({
        summary: summary.data,
        cashflow: cashflow.data || [],
        revenueBreakdown: revCat.data || [],
        expenseBreakdown: expCat.data || [],
        topExpenses: topExp.data || [],
        loading: false,
      });
    } catch (e) {
      this.setState({
        loading: false,
        error:
          e?.response?.data || e.message || "Failed to load accounting data.",
      });
    }
  }

  handleDateChange = (e) => {
    const { name, value } = e.target;
    const parts = value.split("-");
    const d = new Date(
      Number(parts[0]),
      Number(parts[1]) - 1,
      Number(parts[2])
    );
    this.setState({ [name]: d, filter: "custom" });
  };

  formatAmount = (v) =>
    new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(v ?? 0);

  // --- Charts ---
  getCashflowLine = () => {
    const labels = this.state.cashflow.map((p) => {
      const d = new Date(p.date);
      return d.toLocaleString("default", { month: "short", year: "numeric" });
    });
    const rev = this.state.cashflow.map((p) => p.revenue ?? 0);
    const exp = this.state.cashflow.map((p) => p.expense ?? 0);
    const net = this.state.cashflow.map(
      (p) => p.net ?? (p.revenue ?? 0) - (p.expense ?? 0)
    );
    return {
      data: {
        labels,
        datasets: [
          { label: "Revenue", data: rev, borderWidth: 2, tension: 0.25 },
          { label: "Expense", data: exp, borderWidth: 2, tension: 0.25 },
          {
            label: "Net",
            data: net,
            borderWidth: 2,
            borderDash: [6, 4],
            tension: 0.25,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: "bottom" } },
        scales: { y: { beginAtZero: true } },
      },
    };
  };

  getDoughnut = (rows, title) => ({
    data: {
      labels: rows.map((r) => r.category ?? r.label ?? "Uncategorized"),
      datasets: [{ data: rows.map((r) => r.amount ?? r.value ?? 0) }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "bottom" },
        title: { display: true, text: title },
      },
    },
  });

  getTopExpensesBar = () => ({
    data: {
      labels: this.state.topExpenses.map((r) => r.label ?? "Unknown"),
      datasets: [
        {
          label: "Amount",
          data: this.state.topExpenses.map((r) => r.value ?? 0),
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: { y: { beginAtZero: true } },
    },
  });

  render() {
    const {
      loading,
      error,
      summary,
      from,
      to,
      revenueBreakdown,
      expenseBreakdown,
      filter,
    } = this.state;

    return (
      <div className="container-fluid py-3">
        <h3>
          <b>Accounting Summary - Dashboard</b>
        </h3>
        {/* Quick Filters */}
        <Row className="mb-3">
          <Col>
            <ButtonGroup>
              <Button
                variant={filter === "today" ? "primary" : "outline-primary"}
                onClick={() => this.applyFilter("today")}
              >
                Today
              </Button>
              <Button
                variant={filter === "week" ? "primary" : "outline-primary"}
                onClick={() => this.applyFilter("week")}
              >
                This Week
              </Button>
              <Button
                variant={filter === "month" ? "primary" : "outline-primary"}
                onClick={() => this.applyFilter("month")}
              >
                This Month
              </Button>
              <Button
                variant={filter === "year" ? "primary" : "outline-primary"}
                onClick={() => this.applyFilter("year")}
              >
                This Year
              </Button>
              <Button
                variant={filter === "custom" ? "primary" : "outline-primary"}
                onClick={() => this.setState({ filter: "custom" })}
              >
                Custom
              </Button>
            </ButtonGroup>
          </Col>
        </Row>

        {/* Custom Date Range */}
        {filter === "custom" && (
          <Row className="align-items-end mb-3 g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>From</Form.Label>
                <Form.Control
                  type="date"
                  name="from"
                  value={this.iso(from)}
                  onChange={this.handleDateChange}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>To</Form.Label>
                <Form.Control
                  type="date"
                  name="to"
                  value={this.iso(to)}
                  onChange={this.handleDateChange}
                />
              </Form.Group>
            </Col>
            <Col md="auto">
              <Button variant="success" onClick={() => this.loadAll()}>
                Apply
              </Button>
            </Col>
          </Row>
        )}

        {loading && (
          <div className="d-flex align-items-center">
            <Spinner animation="border" className="me-2" />
            <span>Loading accounting insights…</span>
          </div>
        )}
        {error && <Alert variant="danger">{String(error)}</Alert>}

        {!loading && summary && (
          <>
            {/* KPIs */}
            <Row className="g-3">
              <Col md={3}>
                <Card className="shadow-sm border-0">
                  <Card.Body>
                    <div className="text-muted fw-semibold">Total Revenue</div>
                    <div className="fs-3">
                      {this.formatAmount(summary.totalRevenue)}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="shadow-sm border-0">
                  <Card.Body>
                    <div className="text-muted fw-semibold">Total Expense</div>
                    <div className="fs-3">
                      {this.formatAmount(summary.totalExpense)}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="shadow-sm border-0">
                  <Card.Body>
                    <div className="text-muted fw-semibold">Net Profit</div>
                    <div className="fs-3">
                      {this.formatAmount(summary.netProfit)}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="shadow-sm border-0">
                  <Card.Body>
                    <div className="text-muted fw-semibold">AR / AP (Open)</div>
                    <div className="small">
                      AR: {this.formatAmount(summary.outstandingReceivables)}
                    </div>
                    <div className="small">
                      AP: {this.formatAmount(summary.outstandingPayables)}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Charts */}
            <Row className="g-3 mt-1">
              <Col md={8}>
                <Card className="shadow-sm border-0" style={{ height: 380 }}>
                  <Card.Body>
                    <Card.Title className="mb-3">Monthly Cashflow</Card.Title>
                    <div style={{ position: "relative", height: "300px" }}>
                      <Line {...this.getCashflowLine()} />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="shadow-sm border-0" style={{ height: 380 }}>
                  <Card.Body>
                    <div style={{ position: "relative", height: "320px" }}>
                      <Doughnut
                        {...this.getDoughnut(
                          revenueBreakdown,
                          "Revenue by Category"
                        )}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <Row className="g-3 mt-1">
              <Col md={6}>
                <Card className="shadow-sm border-0" style={{ height: 380 }}>
                  <Card.Body>
                    <div style={{ position: "relative", height: "320px" }}>
                      <Doughnut
                        {...this.getDoughnut(
                          expenseBreakdown,
                          "Expense by Category"
                        )}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="shadow-sm border-0" style={{ height: 380 }}>
                  <Card.Body>
                    <Card.Title className="mb-3">
                      Top Expense Buckets
                    </Card.Title>
                    <div style={{ position: "relative", height: "300px" }}>
                      <Bar {...this.getTopExpensesBar()} />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>
    );
  }
}
